import {
  getFunnelSourceFromLocalStorage,
  getGoalWeightFromLocalStorage,
  getMainGoalFromLocalStorage,
  getUseMetricFromLocalStorage,
  getWeightFromLocalStorage,
} from "@helpers/localStorage"
import { LockClosedIcon } from "@heroicons/react/24/solid"
import { useTranslation } from "react-i18next"

type GoalType =
  | "loseWeight"
  | "maintainWeight"
  | "gainWeight"
  | "buildMuscle"
  | "reduceBodyFat"
  | "somethingElse"

const GOAL_TAGS: Record<GoalType, string[]> = {
  loseWeight: [
    "pages.checkout.goalSummary.tags.healthyHabits",
    "pages.checkout.goalSummary.tags.feelConfident",
  ],
  maintainWeight: [
    "pages.checkout.goalSummary.tags.wellBeing",
    "pages.checkout.goalSummary.tags.eatWhatYouLike",
  ],
  gainWeight: [
    "pages.checkout.goalSummary.tags.buildStrength",
    "pages.checkout.goalSummary.tags.moreEnergy",
  ],
  buildMuscle: [
    "pages.checkout.goalSummary.tags.buildStrength",
    "pages.checkout.goalSummary.tags.trackNutrition",
  ],
  reduceBodyFat: [
    "pages.checkout.goalSummary.tags.feelConfident",
    "pages.checkout.goalSummary.tags.trackNutrition",
  ],
  somethingElse: [
    "pages.checkout.goalSummary.tags.wellBeing",
    "pages.checkout.goalSummary.tags.healthyHabits",
  ],
}

const GOAL_EMOJI_MAP: Record<GoalType, string[]> = {
  loseWeight: ["💪", "✨"],
  maintainWeight: ["😊", "🍽️"],
  gainWeight: ["🏋️", "⚡"],
  buildMuscle: ["🏋️", "📊"],
  reduceBodyFat: ["🔥", "📊"],
  somethingElse: ["😊", "💪"],
}

export default function GoalSummary() {
  const { t } = useTranslation()

  const mainGoal = (getMainGoalFromLocalStorage() ?? "loseWeight") as GoalType
  const currentWeight = getWeightFromLocalStorage()
  const goalWeight = getGoalWeightFromLocalStorage()
  const isMetric = getUseMetricFromLocalStorage()

  const unit = isMetric ? t("kg") : t("lb")
  const goalWeightNum = goalWeight ? parseFloat(goalWeight) : null
  const currentWeightNum = currentWeight ? parseFloat(currentWeight) : null

  const getGoalVerb = (): string => {
    if (!goalWeightNum || !currentWeightNum)
      return t("pages.checkout.goalSummary.reach")
    if (goalWeightNum < currentWeightNum)
      return t("pages.checkout.goalSummary.lose")
    if (goalWeightNum > currentWeightNum)
      return t("pages.checkout.goalSummary.gain")
    return t("pages.checkout.goalSummary.maintain")
  }

  const isWhatsappFunnel = getFunnelSourceFromLocalStorage() === "whatsapp"
  const baseTags = GOAL_TAGS[mainGoal] ?? GOAL_TAGS.somethingElse
  const baseEmojis = GOAL_EMOJI_MAP[mainGoal] ?? GOAL_EMOJI_MAP.somethingElse

  const tags = isWhatsappFunnel
    ? [...baseTags, "pages.checkout.goalSummary.tags.whatsAppCoaching"]
    : baseTags
  const emojis = isWhatsappFunnel ? [...baseEmojis, "💬"] : baseEmojis

  return (
    <div className="flex flex-col items-center">
      <div className="mb-5 flex flex-wrap justify-center gap-2">
        {tags.map((tagKey, i) => (
          <span
            key={tagKey}
            className="bg-custom-teal-light text-custom-charcoal inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold"
          >
            <span>{emojis[i]}</span>
            {t(tagKey)}
          </span>
        ))}
      </div>

      <h2 className="text-custom-charcoal text-center text-xl font-bold sm:text-2xl">
        {t("pages.checkout.goalSummary.withYourPlan")}
      </h2>

      {goalWeightNum && (
        <p className="text-custom-teal mt-1 text-center text-3xl font-extrabold sm:text-4xl">
          {getGoalVerb()} {goalWeightNum} {unit}
        </p>
      )}

      {currentWeightNum && goalWeightNum && (
        <div className="mt-6 w-full max-w-sm">
          <WeightGraph
            currentWeight={currentWeightNum}
            goalWeight={goalWeightNum}
            unit={unit}
          />
        </div>
      )}

      <button
        type="button"
        onClick={() =>
          document
            .getElementById("plans")
            ?.scrollIntoView({ behavior: "smooth" })
        }
        className="mt-6 flex w-full cursor-pointer flex-col items-center gap-1 rounded-2xl bg-white p-5 transition-opacity hover:opacity-80"
      >
        <p className="text-custom-charcoal text-center text-lg font-bold">
          {t("pages.checkout.goalSummary.dailyCalorieTarget")}
        </p>
        <div className="flex items-center gap-2">
          <LockClosedIcon className="h-5 w-5 text-gray-400" />
          <span className="text-custom-teal text-2xl font-extrabold">
            {t("pages.checkout.goalSummary.kcal")}
          </span>
        </div>
        <p className="text-sm text-gray-400">
          {t("pages.checkout.goalSummary.startToUnlock")}
        </p>
      </button>
    </div>
  )
}

function WeightGraph({
  currentWeight,
  goalWeight,
  unit,
}: {
  currentWeight: number
  goalWeight: number
  unit: string
}) {
  const isLosing = goalWeight < currentWeight
  const isGaining = goalWeight > currentWeight

  const startY = 50
  const endY = isLosing ? 85 : isGaining ? 25 : 50

  return (
    <div className="relative">
      <svg
        viewBox="0 0 300 120"
        className="w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#7AF2A8" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#7AF2A8" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        <path
          d={`M 20 ${startY} L 280 ${endY} L 280 115 L 20 115 Z`}
          fill="url(#areaGrad)"
        />

        <path
          d={`M 20 ${startY} L 280 ${endY}`}
          fill="none"
          stroke="#7AF2A8"
          strokeWidth="3"
          strokeLinecap="round"
        />

        <circle cx="20" cy={startY} r="6" fill="#7AF2A8" />
        <circle cx="280" cy={endY} r="6" fill="#7AF2A8" />

        <text
          x="20"
          y="112"
          textAnchor="start"
          className="fill-gray-400 text-[11px]"
        >
          {currentWeight} {unit}
        </text>
      </svg>

      <div
        className="absolute right-0 rounded-xl border border-gray-200 bg-white px-3 py-1.5 shadow-sm"
        style={{ top: `${(endY / 120) * 100 - 18}%` }}
      >
        <p className="text-custom-charcoal text-sm font-bold">
          {goalWeight} {unit}
        </p>
      </div>
    </div>
  )
}
